import {
  COURSE_RESOURCES_LOADING,
  RECEIVE_COURSE_RESOURCES,
  RECEIVE_COURSE_RESOURCE,
  REMOVE_COURSE_RESOURCE,
  COURSE_RESOURCES_ERROR,
} from "../actions/course_resources_actions";

const initialState = {
  byId: {},
  allIds: [],
  byCourseKey: {}, // ✅ key -> [resourceIds]
  isLoading: false,
  error: "",
};

const uniq = (arr) => Array.from(new Set(arr));

export default function courseResourcesReducer(state = initialState, action) {
  switch (action.type) {
    case COURSE_RESOURCES_LOADING:
      return { ...state, isLoading: !!action.isLoading };

    case COURSE_RESOURCES_ERROR:
      return { ...state, error: action.error || "" };

    case RECEIVE_COURSE_RESOURCES: {
      const resources = Array.isArray(action.resources) ? action.resources : [];
      const courseKey = action.courseKey; // may be undefined if staff fetches “all”

      const nextById = { ...state.byId };
      const nextAllIds = new Set(state.allIds || []);

      resources.forEach((r) => {
        if (!r?._id) return;
        nextById[r._id] = r;
        nextAllIds.add(r._id);
      });

      // ✅ If this fetch was for a specific courseKey, track that list separately
      const nextByCourseKey = { ...(state.byCourseKey || {}) };
      if (courseKey) {
        nextByCourseKey[courseKey] = resources.map((r) => r._id).filter(Boolean);
      } else {
        // staff “all” fetch: rebuild mappings based on each resource.courseKeys
        const rebuilt = {};
        resources.forEach((r) => {
          (r.courseKeys || []).forEach((k) => {
            if (!rebuilt[k]) rebuilt[k] = [];
            rebuilt[k].push(r._id);
          });
        });
        Object.keys(rebuilt).forEach((k) => (rebuilt[k] = uniq(rebuilt[k])));
        // merge into existing (don’t destroy other keys that might already exist)
        Object.assign(nextByCourseKey, rebuilt);
      }

      return {
        ...state,
        byId: nextById,
        allIds: Array.from(nextAllIds),
        byCourseKey: nextByCourseKey,
      };
    }

    case RECEIVE_COURSE_RESOURCE: {
      const r = action.resource;
      if (!r?._id) return state;

      const nextById = { ...state.byId, [r._id]: r };
      const nextAllIds = uniq([...(state.allIds || []), r._id]);

      // update byCourseKey membership (important when editing courseKeys)
      const nextByCourseKey = { ...(state.byCourseKey || {}) };

      // remove id from all existing keys first
      Object.keys(nextByCourseKey).forEach((k) => {
        nextByCourseKey[k] = (nextByCourseKey[k] || []).filter((id) => id !== r._id);
      });

      // add id to current keys
      (r.courseKeys || []).forEach((k) => {
        nextByCourseKey[k] = uniq([...(nextByCourseKey[k] || []), r._id]);
      });

      return { ...state, byId: nextById, allIds: nextAllIds, byCourseKey: nextByCourseKey };
    }

    case REMOVE_COURSE_RESOURCE: {
      const id = action.id;
      const nextById = { ...state.byId };
      delete nextById[id];

      const nextAllIds = (state.allIds || []).filter((x) => x !== id);

      const nextByCourseKey = { ...(state.byCourseKey || {}) };
      Object.keys(nextByCourseKey).forEach((k) => {
        nextByCourseKey[k] = (nextByCourseKey[k] || []).filter((x) => x !== id);
      });

      return { ...state, byId: nextById, allIds: nextAllIds, byCourseKey: nextByCourseKey };
    }

    default:
      return state;
  }
}
